import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

export type ChatSide = 'left' | 'right'

export interface ChatMessage {
    id: string
    side: ChatSide
    text?: string
    content?: ReactNode
}

export interface ChatPersona {
    initials: string
    name: string
    role: string
}

export const ChatAvatar: FC<{ initials: string, className?: string }> = ({ initials, className }): ReactNode => {
    return (
        <span
            aria-hidden='true'
            className={cn(
                'grid size-7 shrink-0 place-items-center rounded-full bg-[linear-gradient(135deg,#ffb89c,#c9bbff_55%,#8fc9ff)] text-[10px] font-semibold tracking-wide text-ink',
                className
            )}
        >
            {initials}
        </span>
    )
}

const Bubble: FC<{ message: ChatMessage }> = ({ message }): ReactNode => {
    const left = message.side === 'left'

    return (
        <div className={cn('flex animate-bubble-in', left ? 'justify-start' : 'justify-end')}>
            <div
                className={cn(
                    'max-w-[88%] rounded-[22px] border px-4 py-2.5 text-[14px] leading-relaxed sm:max-w-[82%] sm:px-5 sm:py-3 sm:text-[15px]',
                    left ? 'border-white/[0.09] bg-white/[0.035] text-white/85' : 'border-white/[0.14] bg-white/[0.085] text-white'
                )}
            >
                {message.text}
                {message.content}
            </div>
        </div>
    )
}

export const TypingBubble: FC<{ side: ChatSide }> = ({ side }): ReactNode => {
    return (
        <div className={cn('flex animate-bubble-in', side === 'left' ? 'justify-start' : 'justify-end')}>
            <div
                role='status'
                aria-label='Typing'
                className={cn(
                    'flex h-10 items-center gap-1 rounded-[22px] border px-4',
                    side === 'left' ? 'border-white/[0.09] bg-white/[0.035]' : 'border-white/[0.14] bg-white/[0.085]'
                )}
            >
                {[0, 1, 2].map(dot => (
                    <span
                        key={dot}
                        className='size-1.5 rounded-full bg-white/80 animate-typing-dot'
                        style={{ animationDelay: `${dot * 160}ms` }}
                    />
                ))}
            </div>
        </div>
    )
}

interface ChatCardProps {
    persona: ChatPersona
    messages: Array<ChatMessage>
    typing: ChatSide | null
    label: string
    children?: ReactNode
    inline?: ReactNode
    docLines?: Array<string>
    docKey?: string
    className?: string
    logClassName?: string
}

/**
 * The dark conversation card: optional faint document header, a persona
 * line, the haze-lit card with pill bubbles, and a composer slot at the bottom.
 */
export const ChatCard: FC<ChatCardProps> = ({
    persona,
    messages,
    typing,
    label,
    children,
    inline,
    docLines,
    docKey,
    className,
    logClassName
}): ReactNode => {
    const logRef = useRef<HTMLDivElement>(null)
    const reduced = useReducedMotion()
    const hasInline = Boolean(inline)

    useEffect(() => {
        const log = logRef.current

        if (!log || log.scrollHeight <= log.clientHeight) return

        log.scrollTo({ top: log.scrollHeight, behavior: reduced ? 'auto' : 'smooth' })
    }, [messages.length, typing, hasInline, reduced])

    return (
        <div className={cn('relative', className)}>
            {docLines && (
                <div key={docKey} className='mb-8 space-y-1 text-center animate-fade-soft' aria-hidden='true'>
                    {docLines.map(line => (
                        <p key={line} className='eyebrow text-[10px] text-white/25 sm:text-[11px]'>
                            {line}
                        </p>
                    ))}
                </div>
            )}

            <div className='mb-5 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 text-sm'>
                <ChatAvatar initials={persona.initials} />

                <span className='font-medium text-white'>
                    {persona.name}
                </span>

                <span aria-hidden='true' className='size-1 rounded-full bg-white/25' />

                <span className='text-mist'>
                    {persona.role}
                </span>
            </div>

            <div className='relative'>
                <div aria-hidden='true' className='pointer-events-none absolute -top-8 left-1/2 h-24 w-[78%] -translate-x-1/2 rounded-full bg-aurora opacity-30 blur-3xl' />

                <div className='relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-charcoal-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_40px_80px_-40px_rgba(0,0,0,0.9)]'>
                    <div aria-hidden='true' className='pointer-events-none absolute inset-x-10 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(255,184,156,0.55),rgba(201,187,255,0.55),rgba(143,201,255,0.55),transparent)]' />

                    <div
                        ref={logRef}
                        role='log'
                        aria-live='polite'
                        aria-label={label}
                        className={cn('no-scrollbar flex max-h-[440px] min-h-[320px] flex-col gap-3 overflow-y-auto px-4 pb-6 pt-6 sm:px-6', logClassName)}
                    >
                        {messages.map(message => (
                            <Bubble key={message.id} message={message} />
                        ))}

                        {typing && <TypingBubble side={typing} />}

                        {inline}
                    </div>

                    {children && (
                        <div className='border-t border-white/[0.06] p-3 sm:p-4'>
                            {children}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
